import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Shield, TrendingUp, BellRing, CheckCircle, ArrowRight, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Landing = () => {
  const features = [
    {
      icon: <FileText className="w-14 h-14" />,
      title: "Análisis Profundo",
      description: "Identificamos cláusulas importantes, riesgos ocultos, obligaciones y costos reales de tu contrato."
    },
    {
      icon: <Shield className="w-14 h-14" />,
      title: "Seguridad y Confidencialidad",
      description: "Tus documentos se procesan con cifrado y no se comparten con terceros."
    },
    {
      icon: <TrendingUp className="w-14 h-14" />,
      title: "Comparación Inteligente",
      description: "Compara hasta 3 contratos y recibe recomendaciones claras para elegir la mejor opción."
    },
    {
    icon: <BellRing className="w-14 h-14" />,
    title: "Alertas y Seguimiento",
    description: "Recibe recordatorios automáticos sobre vencimientos y fechas clave, con integración a Google Calendar para mantener tus contratos bajo control."
    }
  ];

  const benefits = [
    "Claridad antes de firmar cualquier documento",
    "Lenguaje simple, sin jerga legal innecesaria",
    "Identifica cláusulas de riesgo ocultas",
    "Extrae tasas de interés, CAT y costos automáticamente",
    "Reportes descargables en PDF",
    "NO sustituye abogados, pero te ayuda a entender y evitar errores costosos"
  ];

  return (
    <div className="min-h-screen" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Navigation - Logo más grande y visible */}
      <nav className="border-b border-border bg-white sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-24"> {/* h-20 a h-24 para el logo */}
            <div className="flex items-center gap-4">
              <img src="/assets/logo.png" alt="contractcheck360" className="h-[49px] md:h-[66px] w-auto object-contain" />
              <span className="text-xl md:text-2xl font-bold tracking-tight" style={{ color: '#1A4D2E', fontFamily: 'Merriweather, serif' }}>
                ContractCheck360
              </span>
            </div>
            <div className="flex gap-4 items-center">
              <Link to="/login">
                <Button variant="ghost" className="text-base font-medium px-6 h-11" style={{ color: '#1A4D2E' }}>
                  Iniciar Sesión
                </Button>
              </Link>
              <Link to="/register">
                <Button className="text-base font-semibold h-11 px-8 rounded-lg shadow-md transition-all hover:shadow-lg active:scale-95" style={{ backgroundColor: '#1A4D2E' }}>
                  Registrarse
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 z-0" style={{ backgroundImage: 'url(https://images.pexels.com/photos/3760067/pexels-photo-3760067.jpeg)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.98) 100%)' }} />
        </div>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10 text-center">
          <div className="flex justify-center mb-10">
            <img src="/assets/logo.png" alt="contractcheck360" className="h-[205px] md:h-[231px] w-auto drop-shadow-sm" />
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight font-black mb-8 leading-tight" style={{ fontFamily: 'Merriweather, serif', color: '#1A4D2E' }}>
            Entiende Tus Contratos en Minutos
          </h1>
          <p className="text-xl md:text-2xl leading-relaxed mb-10 max-w-3xl mx-auto" style={{ color: '#334155' }}>
            Sube tu contrato y obtén un análisis claro de riesgos, obligaciones y puntos clave en minutos.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <Link to="/register">
              <Button className="text-lg font-semibold h-14 px-10 rounded-lg shadow-lg" style={{ backgroundColor: '#1A4D2E' }}>
                Analizar mi contrato ahora
                <ArrowRight className="ml-3 w-5 h-5" />
              </Button>
            </Link>
            <Button variant="outline" className="text-lg font-medium h-14 px-10 rounded-lg border-2" style={{ borderColor: '#1A4D2E', color: '#1A4D2E' }} onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}>
              <Play className="mr-2 w-5 h-5" /> Ver cómo funciona
            </Button>
          </div>
          <div className="mt-8 text-center">
            <p 
              className="text-2xl md:text-3xl font-black"
              style={{ color: '#1A4D2E' }}
            >
              Análisis desde $59.90 MXN
            </p>
          
            <p 
              className="text-sm mt-2"
              style={{ color: '#64748B' }}
            >
              🔒 Pago único · Sin suscripciones · Pago seguro con Stripe
            </p>
          </div>        
        </div>
      </section>

      {/* Features Section - Logo eliminado, espaciado respetado */}
      <section id="features" className="py-20 md:py-28" style={{ backgroundColor: '#F5F5F0' }}>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-16">
            {/* Logo de esta sección eliminado */}
            <h2 className="text-3xl sm:text-4xl md:text-5xl tracking-tight font-bold mb-6" style={{ fontFamily: 'Merriweather, serif', color: '#1A4D2E' }}>
              Características Principales
            </h2>
            <p className="text-lg md:text-xl leading-relaxed max-w-2xl mx-auto" style={{ color: '#475569' }}>
              Tecnología de vanguardia para simplificar contratos complejos
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
            {features.map((feature, index) => (
              <div key={index} className="bg-white border border-border shadow-sm hover:shadow-lg transition-all rounded-xl p-8 md:p-10">
                <div className="mb-6" style={{ color: '#1A4D2E' }}>{feature.icon}</div>
                <h3 className="text-2xl md:text-3xl font-semibold tracking-tight mb-4" style={{ fontFamily: 'Merriweather, serif', color: '#0F172A' }}>
                  {feature.title}
                </h3>
                <p className="text-lg leading-relaxed" style={{ color: '#475569' }}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            <div className="flex flex-col justify-center">
              <h2 className="text-3xl sm:text-4xl md:text-5xl tracking-tight font-bold mb-6" style={{ fontFamily: 'Merriweather, serif', color: '#1A4D2E' }}>
                ¿Por Qué ContractCheck360?
              </h2>
              <div className="space-y-5">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <CheckCircle className="w-7 h-7 flex-shrink-0 mt-0.5" style={{ color: '#1A4D2E' }} />
                    <p className="text-lg leading-relaxed" style={{ color: '#334155' }}>{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-center">
              <div className="rounded-2xl overflow-hidden shadow-2xl w-full">
                <img src="https://images.pexels.com/photos/5561911/pexels-photo-5561911.jpeg" alt="Análisis de contratos" className="w-full h-auto" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Logo eliminado y pregunta centrada */}
      <section className="py-20 md:py-28" style={{ backgroundColor: '#1A4D2E' }}>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          {/* Logo eliminado de esta sección */}
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight font-bold mb-8 text-white" style={{ fontFamily: 'Merriweather, serif' }}>
            ¿Listo para Tomar Decisiones Informadas?
          </h2>
          <p className="text-xl md:text-2xl mb-10 max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.9)' }}>
            Únete a quienes ya protegen sus intereses con inteligencia legal.
          </p>
          <Link to="/register">
            <Button className="text-lg font-semibold h-14 px-12 rounded-lg shadow-lg" style={{ backgroundColor: '#FFFFFF', color: '#1A4D2E' }}>
              Analizar mi contrato ahora
              <ArrowRight className="ml-3 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer - URL cambiada por Soporte: mailto */}
      <footer className="border-t border-border py-12" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <img src="/assets/logo.png" alt="contractcheck360" className="h-[77px] w-auto mx-auto mb-6" />
          
          <div className="mb-6">
            <p className="text-base font-medium">
              Soporte: <a href="mailto:contacto@contractcheck360.com" className="underline hover:opacity-80 transition-all" style={{ color: '#1A4D2E' }}>
                contacto@contractcheck360.com
              </a>
            </p>
          </div>

          <div className="flex justify-center gap-6 mb-8 text-sm font-medium" style={{ color: '#64748B' }}>
            <Link to="/privacidad" className="hover:underline">Aviso de Privacidad</Link>
            <Link to="/terminos" className="hover:underline">Términos y Condiciones</Link>
          </div>

          <p className="text-sm text-gray-400">
            © 2026 ContractCheck360. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
